import {
  CardsMarketplaceOffer,
  CardsMarketplaceOfferPromos,
} from 'src/types/marketplace';
import { MethodologyPoints } from '../constants';
import { CalculatePoints, Question, MatchScore, ItemizedScore } from './types';
import { InputsReducer } from 'src/redux/reducers/inputs';
import { IntroAPROrRewards } from 'src/types/inputs';
import _ from 'lodash';
import { isNewBuildCreditFlowExperimentActive } from 'src/lib/experiments';
import { ExperimentsReducer } from 'src/redux/reducers/experiments';

const {
  TWENTY_ONE_POINTS,
  THIRTEEN_POINTS,
  EIGHT_POINTS,
  FIVE_POINTS,
  THREE_POINTS,
  ZERO_POINTS,
} = MethodologyPoints;

export const calculateAnnualFeesScore: CalculatePoints = (
  { annualFees },
  offer: CardsMarketplaceOffer
) => {
  const offerAnnualFee = offer?.details?.annual_fee;
  const offerFirstYearAnnualFee = offer?.details?.annual_fee_first_year;

  if (annualFees === undefined) {
    return ZERO_POINTS;
  }

  const hasNoFee =
    Math.min(offerAnnualFee || 0, offerFirstYearAnnualFee || 0) === 0;

  if (hasNoFee) {
    if (!offerAnnualFee) {
      return THIRTEEN_POINTS;
    }
    if (!offerFirstYearAnnualFee) {
      return EIGHT_POINTS;
    }
  } else if (annualFees) {
    return FIVE_POINTS;
  }

  return ZERO_POINTS;
};

export const calculateCardDeposit: CalculatePoints = (
  { cardDeposit, securityDeposit },
  offer: CardsMarketplaceOffer,
  experiments?: ExperimentsReducer
): number => {
  const offerDeposit = offer?.details?.minimum_deposit;

  if (experiments && isNewBuildCreditFlowExperimentActive(experiments)) {
    if (offerDeposit && securityDeposit && securityDeposit >= offerDeposit) {
      return THIRTEEN_POINTS;
    }
  }

  if (cardDeposit === undefined) {
    return ZERO_POINTS;
  }

  if (cardDeposit) {
    if (offerDeposit === null || offerDeposit === undefined) {
      return ZERO_POINTS;
    }
    return offerDeposit > 0 ? THIRTEEN_POINTS : ZERO_POINTS;
  }

  if (offerDeposit === null) {
    return THIRTEEN_POINTS;
  }
  return offerDeposit === 0 ? THIRTEEN_POINTS : ZERO_POINTS;
};

export const calculateScore = (
  answers: InputsReducer,
  offer: CardsMarketplaceOffer,
  questions: Question[],
  experiments?: ExperimentsReducer
): MatchScore => {
  const itemizedScore = questions.map(({ calculate, availablePoints }) => {
    const awardedPoints = calculate(answers, offer, experiments);
    return {
      awardedPoints,
      availablePoints,
      name: calculate.name,
    } as ItemizedScore;
  });
  return {
    score: itemizedScore.reduce(
      (acc, { awardedPoints, availablePoints }) => {
        if (awardedPoints === null) {
          return acc;
        }

        return {
          awardedPoints: acc.awardedPoints + awardedPoints,
          availablePoints: acc.availablePoints + availablePoints,
        };
      },
      { availablePoints: 0, awardedPoints: 0 }
    ),
    itemizedScore,
  };
};

export const calculateMonthlySpend: CalculatePoints = (
  { monthlySpend },
  { details: { promos } }: CardsMarketplaceOffer
) =>
  promos?.some(
    (promo: CardsMarketplaceOfferPromos) =>
      monthlySpend &&
      promo?.spend_months &&
      monthlySpend * promo?.spend_months >= (promo?.spend_amount ?? 0)
  )
    ? THIRTEEN_POINTS
    : ZERO_POINTS;

export const calculateIntroAPR: CalculatePoints = (
  { introAPROrRewards },
  offer: CardsMarketplaceOffer
) => {
  const offerIntroTime = offer?.details?.purchase_intro_time;
  if (_.isNil(offerIntroTime)) {
    return ZERO_POINTS;
  }

  if (introAPROrRewards === IntroAPROrRewards.IntroAPR) {
    if (offerIntroTime >= 21) {
      return TWENTY_ONE_POINTS;
    }
    if (offerIntroTime >= 18 && offerIntroTime < 21) {
      return THIRTEEN_POINTS;
    }
    if (offerIntroTime >= 15 && offerIntroTime < 18) {
      return EIGHT_POINTS;
    }
    if (offerIntroTime >= 12 && offerIntroTime < 15) {
      return FIVE_POINTS;
    }
    if (offerIntroTime >= 1 && offerIntroTime < 12) {
      return THREE_POINTS;
    }
  }

  if (introAPROrRewards === IntroAPROrRewards.Rewards) {
    const rewards = offer?.details?.rewards;
    const bonusCategories = rewards?.filter(
      (reward) => reward?.rate && reward.rate >= 1
    );
    const hasBonusCategory = bonusCategories && bonusCategories.length > 0;

    let rewardsPoints = hasBonusCategory ? EIGHT_POINTS : ZERO_POINTS;
    if (offerIntroTime >= 21) {
      rewardsPoints += TWENTY_ONE_POINTS;
    }
    if (offerIntroTime >= 18 && offerIntroTime < 21) {
      rewardsPoints += THIRTEEN_POINTS;
    }
    if (offerIntroTime >= 15 && offerIntroTime < 18) {
      rewardsPoints += EIGHT_POINTS;
    }
    if (offerIntroTime >= 12 && offerIntroTime < 15) {
      rewardsPoints += FIVE_POINTS;
    }
    if (offerIntroTime >= 1 && offerIntroTime < 12) {
      rewardsPoints += THREE_POINTS;
    }
    if (offerIntroTime === 0) {
      rewardsPoints += ZERO_POINTS;
    }
    return rewardsPoints;
  }

  return ZERO_POINTS;
};
